import gql from 'graphql-tag'

export const CashflowFragment = `
  cashflowData(years: $years, options: $cashflowOptions) {
    source
    period
    category
    year
    data
    reason
    resourceType
    createdAt
    accountable {
      ... on Administrator {
        name
      }
      ... on User {
        name
      }
    }
  }
`

const FinancialReviewSegmentsFragment = `
  financialReviewSegments {
    title
    key
    categories {
      key
      title
      required
      derived
    }
  }
`

const TaxReturnsFragment = `
  taxReturns(includeInactive: true) {
    year
    formName
    rid
  }
`

const PersonalFinancialFragment = `
  personalFinancial {
    cashInBanks
    marketableSecurities
    installmentLoans
    creditCards
    otherAssets
    otherLiabilities
    realEstate
    lifeInsurance
    retirementAccounts
    otherMortgages
    taxesOwed
  }
`

const BalanceSheetFragment = `
  balanceSheet {
    rid
    checkingSaving
    fixedAssets
    currentLiabilities
    nonCurrentLiabilities
    totalAssets
    totalLiabilities
    cashEquivalents
    date
    balanceSheetDate
  }
`

const UseOfFundFragment = `
  useOfFund {
    refinance
    realEstateMortgageRefinance
    realEstatePurchase
    businessAcquisition
    inventory
    hiring
    marketing
    operationalCosts
    newEquipment
    remaining
    total
  }
`

const PersonalFiscalYearFragment = `
  fiscalYears {
    rid
    year
    days
    period
    isFullYear
    isLeapYear
    annualizationCoefficient
  }
`

const BusinessFiscalYearFragment = `
  fiscalYears {
    rid
    year
    days
    period
    isFullYear
    isLeapYear
    annualizationCoefficient
  }
`

export default gql`
  query FinancialReviewQuery($loanId: Int!, $years: [Int!]!, $cashflowOptions: CashflowOptions) {
    loan(rid: $loanId, unscoped: true) {
      rid
      needsRedecision
      cashflowDecisionable
      cre
      primeRate
      spread
      term
      amount
      noTranscriptsFlow
      state
      hardCreditPulled
      createdAt
      verification {
        ${UseOfFundFragment}
      }
      ${CashflowFragment}
      ${FinancialReviewSegmentsFragment}
      product {
        rid
        name
        category
        manualMin
        manualMax
        term
        prime
        spread(loanId: $loanId, bestPrice: false)
        rateHash
        tierRates
        monthsRemainingCutoff
        productType
        livingExpense
        federalTaxes
        otherTaxes
        officerCompAddback
        lenderKey
      }
      businesses(includeAffiliates: true) {
        rid
        borrowerType
        applicableYears
        interimYears
        name
        hasExistingDebt
        debtSchedulePending
        businessScore
        timeInBusiness
        balanceSheetUrl
        profitAndLossDocumentUrls {
          value
          label
        }
        taxReturnUrls {
          value
          label
        }
        annualizationCoefficient
        interimPeriods {
          to
          from
          year
        }
        financial {
          fiscalYearEndOn
        }
        ${BusinessFiscalYearFragment}
        ${CashflowFragment}
        ${FinancialReviewSegmentsFragment}
        ${TaxReturnsFragment}
        ${BalanceSheetFragment}
        guarantors {
          rid
          name
          spouseId
          filingStatus
          creditDataUrls
          creditScore
          jointAccountAmount
          reportedRealEstatePayment
          scheduledMonthlyPayment
          interimYears
          business {
            rid
          }
          taxReturnUrls {
            value
            label
          }
          ${PersonalFiscalYearFragment}
          ${CashflowFragment}
          ${FinancialReviewSegmentsFragment}
          ${TaxReturnsFragment}
          ${PersonalFinancialFragment}
        }
        outstandingLoans {
          rid
          active
          refinance
          lender
          creLoan
          debtType
          monthsRemaining
          principalBalance
          originationDate
          maturityDate
          monthlyPayment
          minimumPayment
          paymentFrequency
        }
      }
    }
  }
`
